import clsx from 'clsx'
import first from 'lodash/first'
import forEach from 'lodash/forEach'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import map from 'lodash/map'
import React, {
  Dispatch,
  FC,
  MutableRefObject,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import SvgAngleLeft from '@Assets/SVG/AngleLeft'
import SvgCheck from '@Assets/SVG/Check'
import { useSplitTest } from '@Globals/SplitTests/Hooks'
import { useTranslation } from '@Globals/Translations'
import { SelectInputActionProps, SelectInputValidationFn } from '@Shared/Components/Inputs/Select/SelectInput'

import styles from './SelectInput.module.scss'

export type SelectObjectValidationResult = { errorMessage: string, success: boolean }
export type SelectObjectValidationFn = <T>(value: T) => SelectObjectValidationResult
export type SelectObjectActionProps = {
  touch: () => void
  validate: <T extends Object>(value?: T) => boolean | undefined
}
interface TextInputProps<T extends Object>{
  actionRef?: MutableRefObject<SelectObjectActionProps | SelectInputActionProps | null>
  autoComplete?: string
  className?: string
  defaultTouched?: boolean
  deselect?: T
  disabled?: boolean
  disabledOptions?: T[]
  getObjectLabel: (obj: T) => string
  id?: string
  label?: string | null
  onChange: (value: T, valid: boolean) => void
  options?: T[]
  setValue?: Dispatch<SetStateAction<T>> | ((value: T) => void)
  validations?: Array<SelectObjectValidationFn | SelectInputValidationFn>
  value?: T | null
}
export const SelectObject: FC<PropsWithChildren<TextInputProps<any>>> = <T extends Object>({
  autoComplete,
  actionRef,
  className,
  deselect,
  disabled,
  label,
  value,
  disabledOptions = [],
  id,
  onChange,
  validations = [],
  setValue,
  getObjectLabel,
  defaultTouched = false,
  options,
}) => {
  const cofDesignV1 = useSplitTest('cof_design_v1', 'b')
  const { t } = useTranslation()
  const ref = useRef<HTMLSelectElement | null>(null)
  const isOpen = useRef<boolean>(false)
  const [touched, setTouched] = useState(defaultTouched)
  const [errors, setErrors] = useState<Array<SelectObjectValidationResult>>([])
  const hasError = useMemo(() => touched && validations.length > 0 && errors.length > 0, [errors, touched, validations.length])
  const isValid = useMemo(() => touched && validations.length > 0 && errors.length === 0, [errors, touched, validations.length])

  const runValidations: (newValue?: T) => boolean = useCallback(
    (newValue?: T) => {
      if (!isNil(newValue) && setValue) {
        setValue(newValue)
      }
      const validationErrors: Array<SelectObjectValidationResult> = []
      forEach(validations, (validationFn: SelectObjectValidationFn) => {
        const result: SelectObjectValidationResult = validationFn(newValue || value)
        if (!result.success) {
          validationErrors.push(result)
        }
      })
      setErrors(validationErrors)
      isOpen.current = false
      return validationErrors.length === 0
    },
    [value, validations, setValue],
  )

  useImperativeHandle(actionRef, () => ({
    validate: () => runValidations(),
    touch: () => setTouched(true),
  }))

  const handleChange = useCallback((newValue: T) => {
    setTouched(true)
    isOpen.current = false
    onChange(newValue, runValidations(newValue))
  }, [onChange, runValidations])

  useEffect(() => {
    if (!isEmpty(value)) {
      runValidations()
    }
    if (defaultTouched) {
      runValidations(value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getObjectValue = useCallback(
    (eventValue?: string | number | null) => options?.find(o => getObjectLabel(o) === eventValue),
    [getObjectLabel, options],
  )

  return (
    <div
      className={clsx(
        styles.inputContainer,
        hasError && styles.error,
        disabled && styles.disabled,
        !disabled && isValid && styles.success,
        className,
        cofDesignV1 && styles.cofDesignV1,
      )}
    >
      <select
        aria-label={typeof label === 'string' ? label : undefined}
        aria-labelledby={`${id}-label`}
        autoComplete={autoComplete}
        className={clsx(styles.inputField, !isEmpty(value) && styles.inputFieldWithValue, !isNil(label) && styles.withLabel)}
        disabled={disabled}
        id={id}
        onChange={e => handleChange(getObjectValue(e.target.value))}
        onClick={() => {
          isOpen.current = true
        }}
        ref={ref}
        value={getObjectLabel(value) ?? getObjectLabel(first(options))}
      >
        {deselect && <option value={deselect}>{getObjectLabel(deselect)}</option>}
        {map(options, (option: T) => (
          <option
            key={getObjectLabel(option)}
            disabled={disabledOptions.map(o => getObjectLabel(o)).includes(getObjectLabel(option))}
            value={getObjectLabel(option)}
          >
            {t(getObjectLabel(option))}
          </option>
        ))}

      </select>
      <label
        className={clsx(
          styles.inputLabel,
          !!value && styles.labelUp,
        )}
        htmlFor={id}
        id={`${id}-label`}
      >
        {label}
      </label>
      <div className={clsx(styles.arrow, disabled && styles.disabled)}>
        {!isValid && (
          <SvgAngleLeft className={clsx(
            !!isOpen.current && styles.isOpen,
            !isOpen.current && styles.isClosed,
          )}
          />
        )}
      </div>
      {!cofDesignV1 && !disabled && isValid && <div className={styles.checkmark}><SvgCheck /></div>}
      {hasError && (
        <span className={clsx(styles.error, 'error')}>
          {cofDesignV1 && (
            <>
              {t('common:ERROR_LABEL')}
              {': '}
            </>
          )}
          {first(errors)!.errorMessage}
        </span>
      )}
    </div>
  )
}
